import "./index.scss";

import { Box, Grid, MenuItem, Stack, TextareaAutosize } from "@mui/material";
import { ReactComponent as IconRightArrow } from "assets/images/square-buttons/arrow-right.svg";
import Dropdown from "components/Dropdown";
import Input from "components/Input";
import ManagedStringList from "components/ManagedStringList";
import SearchableList from "components/SearchableList";
import _ from "lodash";
import { memo, useContext, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { toast } from "react-toastify";

import { WorkoutPlanContext } from "..";
import CopyFromMemberModal from "../CopyFromMemberModal";
import CopyFromTemplateModal from "../CopyFromTemplateModal";

const PlanInfo = ({
  goalOptions,
  loadWorkoutPlanFromCopy,
  loadWorkoutPlanFromMemberCopy,
  isTemplate = true,
}) => {
  const workoutPlanContext = useContext(WorkoutPlanContext);
  const formContext = useFormContext();
  const {
    register,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = formContext;

  const [copyFromOpenDefault, setCopyFromOpenDefault] = useState(`none`);
  const [optionsGoals, setOptionsGoals] = useState(goalOptions);

  const [isFlexibilityProgramIncluded, setIsFlexibilityProgramIncluded] = useState(
    getValues("isFlexibilityProgramIncluded")
  );
  const [isCardioProgramIncluded, setIsCardioProgramIncluded] = useState(
    getValues("isCardioProgramIncluded")
  );
  const [isStrengthProgramIncluded, setIsStrengthProgramIncluded] = useState(
    getValues("isStrengthProgramIncluded")
  );
  const [hasFlexibilityProgramBeenSaved, setHasFlexibilityProgramBeenSaved] = useState(
    getValues("hasFlexibilityProgramBeenSaved")
  );
  const [hasCardioProgramBeenSaved, setHasCardioProgramBeenSaved] = useState(
    getValues("hasCardioProgramBeenSaved")
  );
  const [hasStrengthProgramBeenSaved, setHasStrengthProgramBeenSaved] = useState(
    getValues("hasStrengthProgramBeenSaved")
  );
  const [showCopyFromMemberModal, setShowCopyFromMemberModal] = useState(false);
  const [showCopyFromTemplateModal, setShowCopyFromTemplateModal] = useState(false);
  const watchGoals = watch("goals");
  const watchCustomGoals = watch("additional_goals");

  const [workoutPlanNameCount, setWorkoutPlanNameCount] = useState(0);
  const workoutPlanNameLength = 31;

  const [instructorNotesCount, setInstructorNotesCount] = useState(0);
  const instructorNotesLength = 600;

  const [warmUpNotesCount, setWarmUpNotesCount] = useState(0);
  const warmUpNotesLength = 600;

  const removalMessage = "You cannot remove a program once it is included in a workout plan";

  const toggleFlexibility = () => {
    if (hasFlexibilityProgramBeenSaved) {
      toast(removalMessage, { type: "error" });
    } else {
      setIsFlexibilityProgramIncluded(!isFlexibilityProgramIncluded);
      setValue("isFlexibilityProgramIncluded", !isFlexibilityProgramIncluded);
    }
  };

  const toggleCardio = () => {
    if (hasCardioProgramBeenSaved) {
      toast(removalMessage, { type: "error" });
    } else {
      setIsCardioProgramIncluded(!isCardioProgramIncluded);
      setValue("isCardioProgramIncluded", !isCardioProgramIncluded);
    }
  };

  const toggleStrength = () => {
    if (hasStrengthProgramBeenSaved) {
      toast(removalMessage, { type: "error" });
    } else {
      setIsStrengthProgramIncluded(!isStrengthProgramIncluded);
      setValue("isStrengthProgramIncluded", !isStrengthProgramIncluded);
    }
  };

  const handleSelectApplicableGoals = (list) => {
    setValue("goals", list);
  };

  const handleUpdateCustomGoals = (values: string[]) => {
    setValue("additional_goals", values);
  };

  const handleCopyFromMember = (value) => {
    loadWorkoutPlanFromMemberCopy(value);
    setShowCopyFromMemberModal(false);
  };

  const handleCopyFromTemplate = (value) => {
    loadWorkoutPlanFromCopy(value);
    setShowCopyFromTemplateModal(false);
  };

  return (
    <>
      <Stack className="workoutPlanPlanInfo">
        <Box mb={"20px"}>
          <h2 className="headerSpacing">Workout Plan Name</h2>
          <Stack direction={"row"} gap={1.25}>
            <Stack direction={"column"} gap={1}>
              <Box flexGrow={1}>
                <Controller
                  name="workout_plan_name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value, ref } }) => (
                    <Stack spacing={1}>
                      <Input
                        value={value}
                        width="300px"
                        maxLength={workoutPlanNameLength}
                        onChange={(v) => {
                          onChange(v);
                          setWorkoutPlanNameCount(v.target.value.length);
                        }}
                        hasError={!_.isEmpty(errors.workout_plan_name)}
                        placeholder="Enter Name"
                      />
                      {errors.workout_plan_name?.type === "required" && (
                        <h4 className="error">This field is required</h4>
                      )}
                    </Stack>
                  )}
                />
              </Box>
              <Stack direction={"row"} alignItems={"right"} justifyContent={"flex-end"} gap={1}>
                {workoutPlanNameCount}/{workoutPlanNameLength}
              </Stack>
            </Stack>
            {!isTemplate ? (
              <Box sx={{ minWidth: 170 }}>
                <Dropdown
                  fullWidth
                  defaultValue={copyFromOpenDefault}
                  value={copyFromOpenDefault}
                  name="copyFrom"
                  MenuProps={{
                    anchorOrigin: {
                      horizontal: "right",
                      vertical: "bottom",
                    },
                    classes: { paper: "workoutPlanCopyFrom" },
                    transformOrigin: {
                      horizontal: "right",
                      vertical: "top",
                    },
                  }}
                >
                  <MenuItem value="none" disabled>
                    Copy from...
                  </MenuItem>
                  {/* <MenuItem
                  value={``}
                  onClick={() => {
                    setShowCopyFromMemberModal(true);
                    setCopyFromOpenDefault(`none`);
                  }}
                >
                  <span>Copy from Member</span> <IconRightArrow />
                </MenuItem> */}
                  <MenuItem
                    value={``}
                    onClick={() => {
                      setShowCopyFromTemplateModal(true);
                      setCopyFromOpenDefault(`none`);
                    }}
                  >
                    <span>Copy from Template</span> <IconRightArrow />
                  </MenuItem>
                </Dropdown>
              </Box>
            ) : (
              <></>
            )}
          </Stack>
        </Box>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <Box className="applicableGoalsContainer">
              <h2 className="headerSpacing">Define all applicable goals</h2>
              <SearchableList
                preselectedValues={getValues("goals")}
                options={optionsGoals}
                callback={handleSelectApplicableGoals}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Stack height="100%">
              <Box mb={"20px"}>
                <h2 className="headerSpacing">Select programs to include in the workout plan</h2>
                <input type="hidden" {...register("isFlexibilityProgramIncluded")} />
                <input type="hidden" {...register("isCardioProgramIncluded")} />
                <input type="hidden" {...register("isStrengthProgramIncluded")} />
                <Box display="flex" gap={4}>
                  <Box
                    className={"program-card " + (isFlexibilityProgramIncluded ? "selected" : "")}
                    onClick={toggleFlexibility}
                  >
                    <img
                      alt="Flexibility"
                      src={require("assets/images/ui/flexibility.png")}
                      height="58px"
                    />
                    <p>Flexibility</p>
                  </Box>

                  <Box
                    className={"program-card " + (isCardioProgramIncluded ? "selected" : "")}
                    onClick={toggleCardio}
                  >
                    <img
                      alt="Cardiovascular"
                      src={require("assets/images/ui/cardiovascular.png")}
                      height="58px"
                    />
                    <p>Cardiovascular</p>
                  </Box>

                  <Box
                    className={"program-card " + (isStrengthProgramIncluded ? "selected" : "")}
                    onClick={toggleStrength}
                  >
                    <img
                      alt="Strength"
                      src={require("assets/images/ui/strength.png")}
                      height="58px"
                    />
                    <p>Strength</p>
                  </Box>
                </Box>
              </Box>
              {/* Instructor Notes */}
              <Stack>
                <h2 className="headerSpacing">Instructor Notes</h2>
                <Stack flexGrow={1} className="expandTextAreaHeight">
                  <Controller
                    name="instructor_notes"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Stack spacing={1} className="note-input-container">
                        <textarea
                          //{...field}
                          value={value}
                          rows={10}
                          aria-label="description textarea"
                          className="note-textarea"
                          placeholder="Please enter"
                          maxLength={instructorNotesLength}
                          onChange={(v) => {
                            onChange(v);
                            setInstructorNotesCount(v.target.value.length);
                          }}
                        />
                        <Stack
                          direction={"row"}
                          alignItems={"right"}
                          justifyContent={"flex-end"}
                          gap={1}
                        >
                          {instructorNotesCount}/{instructorNotesLength}
                        </Stack>
                      </Stack>
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            {!workoutPlanContext.isWorkoutPlanTemplate && (
              <Box>
                <h2 className="headerSpacing">Add Additional Goals</h2>
                <ManagedStringList
                  list={getValues("additional_goals")}
                  callback={handleUpdateCustomGoals}
                />
                <span className="selectedGoalsSummary">
                  {watchGoals?.length || 0} goal{(watchGoals?.length || 0) !== 1 ? `s` : ``},{" "}
                  {watchCustomGoals?.length || 0} custom goal
                  {(watchCustomGoals?.length || 0) !== 1 ? `s` : ``} selected
                </span>
              </Box>
            )}
          </Grid>
          {/* Warm Up Notes */}
          <Grid item xs={6}>
            <Stack height="100%">
              <h2 className="headerSpacing">Warm Up Notes</h2>
              <Stack className="expandTextAreaHeight" flexGrow={1} mb="36px">
                <Controller
                  name="warm_up_notes"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Stack spacing={1} className="note-input-container">
                      <textarea
                        value={value}
                        rows={10}
                        aria-label="description textarea"
                        className="note-textarea"
                        placeholder="Please enter"
                        maxLength={warmUpNotesLength}
                        onChange={(v) => {
                          onChange(v);
                          setWarmUpNotesCount(v.target.value.length);
                        }}
                      />
                      <Stack
                        direction={"row"}
                        alignItems={"right"}
                        justifyContent={"flex-end"}
                        gap={1}
                      >
                        {warmUpNotesCount}/{warmUpNotesLength}
                      </Stack>
                    </Stack>
                  )}
                />
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
      <CopyFromMemberModal open={showCopyFromMemberModal} onClose={handleCopyFromMember} />
      {showCopyFromTemplateModal && (
        <CopyFromTemplateModal open={showCopyFromTemplateModal} onClose={handleCopyFromTemplate} />
      )}
    </>
  );
};

export default memo(PlanInfo);
